import { FlowBlockValueUuidSchema, FlowDurationSchema, FlowFormV2Mode } from 'flow-schema';
import { z } from 'zod';

import {
  FormBlockBaseConfigShape,
  FormTemplateConfigSchema,
  formBlockEntityOverrideCheck,
  formBlockEntityOverrideRefineMessage,
} from './config.js';
import { isDefined } from '../../../utils.js';
import { BaseFlowBlockSchema, BlockType } from '../base/baseSchema.js';

export function isFormV2WaitMode(mode: FlowFormV2Mode): boolean {
  return mode === FlowFormV2Mode.AddAndWait || mode === FlowFormV2Mode.AddAndWaitForCompletion;
}

// FormV2 = v1 Form + (optional) WaitForFormCompletion fused into one block.
// `mode` controls whether the block waits for the form set to reach a terminal
// state. `timeout` is required whenever the block waits (`AddAndWait` or
// `AddAndWaitForCompletion`).
export const FormV2BlockConfigSchema = FormBlockBaseConfigShape.omit({ runMode: true })
  .extend({
    formTemplateConfigs: z
      .array(FormTemplateConfigSchema)
      .min(1, 'FormV2 requires at least one form template'),
    mode: z.enum(FlowFormV2Mode).optional().default(FlowFormV2Mode.AddAndWaitForCompletion),
    timeout: FlowDurationSchema.optional(),
    notificationSchemeIdOverride: FlowBlockValueUuidSchema({ isRequired: false })
      .nullable()
      .optional(),
  })
  .refine(formBlockEntityOverrideCheck, formBlockEntityOverrideRefineMessage)
  .refine((config) => !isFormV2WaitMode(config.mode) || isDefined(config.timeout), {
    message: 'A timeout is required when the FormV2 block waits for the form set.',
    path: ['timeout'],
  });

export type FormV2BlockConfig = z.infer<typeof FormV2BlockConfigSchema>;

export const FormV2BlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.FormV2),
  config: FormV2BlockConfigSchema,
});
export type FormV2Block = z.infer<typeof FormV2BlockSchema>;
